import type { JSX } from "react";

import isNil from "lodash/isNil";

import BaseCard from "@/ui/layout/BaseCard";
import Box from "@/ui/layout/Box";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";

type ListSeparatorType = "line" | "space";
type SectionSeparatorSize = "md" | "sm";

const ListDimensions = {
  FABBottomOffset: 76,
  HorizontalSeparatorWidth: 18,
  SafeVerticalPadding: 24,
  SeparatorSize: 1,
  VerticalSectionSeparatorHeight: 18,
  VerticalSeparatorHeight: 12,
};

interface ItemSeparatorComponentProps {
  isHorizontal?: boolean;
  separatorPadding?: number;
  separatorType?: ListSeparatorType;
}

interface SectionSeparatorComponentProps {
  isHorizontal?: boolean;
  size?: SectionSeparatorSize;
}

const SpaceSeparatorComponent = ({
  isHorizontal,
  separatorPadding,
  separatorType,
}: ItemSeparatorComponentProps) => {
  const separatorColor = ColorToken.NeutralLine;

  if (isHorizontal) {
    if (separatorType === "line") {
      const paddingHorizontal =
        separatorPadding ?? ListDimensions.HorizontalSeparatorWidth / 2;
      return (
        <Box paddingHorizontal={paddingHorizontal}>
          <Box backgroundColor={separatorColor} flexGrow={1} width={1} />
        </Box>
      );
    }

    return <Box width={ListDimensions.HorizontalSeparatorWidth} />;
  }

  if (separatorType === "line") {
    const paddingVertical =
      separatorPadding ?? ListDimensions.HorizontalSeparatorWidth / 2;
    return (
      <Box paddingVertical={paddingVertical}>
        <Box
          alignSelf="center"
          backgroundColor={separatorColor}
          height={1}
          width="100%"
        />
      </Box>
    );
  }

  return <Box height={ListDimensions.VerticalSeparatorHeight} />;
};

const SectionSeparatorComponent = ({
  isHorizontal,
  size = "sm",
}: SectionSeparatorComponentProps) => {
  if (isHorizontal) {
    return <Box width={ListDimensions.HorizontalSeparatorWidth} />;
  }

  const separatorHeight =
    size === "sm"
      ? ListDimensions.VerticalSectionSeparatorHeight
      : ListDimensions.VerticalSectionSeparatorHeight * 2;

  return <Box height={separatorHeight} />;
};

interface EmptyListComponentProps extends BaseViewProps {
  contentComponent?: () => JSX.Element;
  isEmpty: boolean;
  isLoading?: boolean;
}

const EmptyListComponent = ({
  contentComponent,
  isEmpty,
  isLoading,
  testID,
}: EmptyListComponentProps) => {
  if (!isEmpty || isLoading || isNil(contentComponent)) return null;
  return (
    <BaseCard isBorderDashed testID={testID}>
      {contentComponent()}
    </BaseCard>
  );
};

interface FlatListContainerStylesArgs {
  isFABVisible?: boolean;
  isHorizontal?: boolean;
}

interface FlatListContentStyles {
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
}

const getFlatListContentStyles = ({
  isFABVisible,
  isHorizontal,
}: FlatListContainerStylesArgs): FlatListContentStyles => {
  return {
    paddingBottom: isFABVisible
      ? ListDimensions.FABBottomOffset
      : ListDimensions.SafeVerticalPadding,
    paddingLeft: isHorizontal ? LayoutToken.ContentPaddingHorizontal : 0,
    paddingRight: isHorizontal ? LayoutToken.ContentPaddingHorizontal : 0,
    paddingTop: isHorizontal ? 0 : ListDimensions.SafeVerticalPadding,
  };
};

export {
  EmptyListComponent,
  getFlatListContentStyles,
  ListDimensions,
  SectionSeparatorComponent,
  SpaceSeparatorComponent,
};
export type { ListSeparatorType, SectionSeparatorSize };
